
import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, ResponsiveContainer } from "recharts";
import { COLORS, CustomTooltip } from "./PieChartDisplay";

interface BarChartDisplayProps {
  data: Array<any>;
  dataKey: string;
  nameKey?: string;
  barName?: string;
  height?: number | string;
  layout?: "horizontal" | "vertical";
  margin?: { top: number; right: number; left: number; bottom: number };
}

const BarChartDisplay: React.FC<BarChartDisplayProps> = ({
  data,
  dataKey,
  nameKey = "name",
  barName = "Aantal",
  height = "100%",
  layout = "horizontal",
  margin = { top: 5, right: 30, left: 20, bottom: 5 }
}) => {
  if (layout === "vertical") {
    return (
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: margin.top, right: margin.right, left: 100, bottom: margin.bottom }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number" 
              tick={{ fill: '#4B5563', fontSize: 12 }} // Improved text legibility
              tickLine={{ stroke: '#9CA3AF' }}
            />
            <YAxis 
              type="category" 
              dataKey={nameKey} 
              width={100}
              tick={{ fill: '#4B5563', fontSize: 12 }}
              tickLine={{ stroke: '#9CA3AF' }}
              tickFormatter={(value) => {
                if (typeof value === 'string' && value.length > 12) {
                  return `${value.substring(0, 12)}...`;
                }
                return value;
              }}
            />
            <Tooltip 
              content={<CustomTooltip />} 
              wrapperStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #ccc', 
                borderRadius: '6px',
                boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1)' 
              }}
            />
            <Legend 
              formatter={(value) => <span className="text-sm font-medium">{value}</span>}
              iconType="circle"
            />
            <Bar 
              dataKey={dataKey} 
              name={barName} 
              fill="#0072BC" 
              animationDuration={800} 
              animationEasing="ease-out"
              className="hover:opacity-80 transition-opacity duration-300"
            >
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={COLORS[index % COLORS.length]} 
                  style={{ filter: 'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1))' }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }
  
  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          margin={margin}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey={nameKey}
            tick={{ fill: '#4B5563', fontSize: 12 }}
            tickLine={{ stroke: '#9CA3AF' }}
            padding={{ left: 10, right: 10 }}
            tickFormatter={(value) => {
              if (typeof value === 'string' && value.length > 10) {
                return `${value.substring(0, 10)}...`;
              }
              return value;
            }}
          />
          <YAxis 
            tick={{ fill: '#4B5563', fontSize: 12 }} 
            tickLine={{ stroke: '#9CA3AF' }}
          />
          <Tooltip 
            content={<CustomTooltip />} 
            wrapperStyle={{ 
              backgroundColor: 'white', 
              border: '1px solid #ccc', 
              borderRadius: '6px',
              boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1)' 
            }}
          />
          <Legend 
            formatter={(value) => <span className="text-sm font-medium">{value}</span>}
            iconType="circle"
          />
          <Bar 
            dataKey={dataKey} 
            name={barName} 
            fill="#0072BC" 
            radius={[4, 4, 0, 0]}
            animationDuration={800} 
            animationEasing="ease-out"
            className="hover:opacity-80 transition-opacity duration-300"
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={COLORS[index % COLORS.length]}
                style={{ filter: 'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1))' }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChartDisplay;
